// =============================================================================
// pages/SettingsLifeProfile.js — Phase 74A (post-user-feedback voice pass)
//
// The primary "edit anywhere" surface for the Life Profile bag. Users
// land here from:
//   • Settings → "Life Profile" tile
//   • The Dashboard "Help 2120 understand you" tile
//   • Any "See all questions" link on a progressive prompt
//
// UX principles baked in:
//   • Sectioned scroll, not a wizard — users can skim and answer only
//     what they want.
//   • Save-as-you-go (autosave 400ms after last change) so nothing is
//     lost if they close the tab.
//   • Every option chip is optional and includes "Prefer not to say";
//     PNTS *counts as answered* (privacy = no penalty).
//   • Progress framed as "unlock more personalised insights" — never
//     adversarial.
// =============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Check, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import usePageTitle from '@/hooks/usePageTitle';
import {
  LIFE_PROFILE_CATALOG, PNTS,
  getValue, setValue, shouldShowQuestion,
} from '@/lib/lifeProfile';
import ClarityScoreEmbed from '@/components/insights/ClarityScoreEmbed';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LifeProfileEmbed — the full progressive-disclosure Life Profile experience
 * (autosave, catalog-driven questions, completeness bar, tier badge) rendered
 * WITHOUT the standalone-page chrome (no back button, no outer max-width).
 *
 * Phase 82 — this is imported by the new `/settings` sidebar Settings page
 * so the same authoritative UI serves both the deep-link route
 * (`/settings/life-profile`) and the sidebar section. When the user opens the
 * "Life Profile" tab in Settings, we mount exactly this component.
 *
 * All the underlying data model (LIFE_PROFILE_CATALOG, PNTS handling, tier
 * scoring) lives untouched in lib/lifeProfile and the backend
 * /api/life-profile endpoint — this is a pure view refactor.
 */
export function LifeProfileEmbed({ showTitle = true }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bag, setBag] = useState({});
  const [meta, setMeta] = useState({
    pct: 0,
    tier: 'Basic',
    coreAnswered: 0,
    coreTotal: 0,
  });
  const saveTimer = useRef(null);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const { data } = await axios.get(`${BACKEND_URL}/api/life-profile`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
          timeout: 10000,
        });
        setBag(data?.life_profile || {});
        setMeta({
          pct:          Number(data?.completeness || 0),
          tier:         String(data?.profile_quality || 'Basic'),
          coreAnswered: Number(data?.core_answered || 0),
          coreTotal:    Number(data?.core_total || 0),
        });
      } catch (e) {
        const status = e?.response?.status;
        if (status === 503) {
          toast.error("We're finishing a quick database update. Please try again in a minute.");
        } else {
          console.warn('life-profile load failed', e);
          toast.error('Could not load your profile. Please retry.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Debounced autosave — merges just the deltas.
  const scheduleSave = (nextBag) => {
    setBag(nextBag);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(nextBag), 400);
  };

  const save = async (nextBag) => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/life-profile`,
        { life_profile: nextBag },
        // v3.7.12 Phase 84.31 Part 6 — 10s was too tight for Prod
        // cold-start latency: users saw "Could not save just now" even
        // though the JSONB write had actually landed. Bumping to 30s
        // covers real-world worst case; question-feed regeneration was
        // moved to a background task so the endpoint returns fast.
        { headers: { Authorization: `Bearer ${session?.access_token}` }, timeout: 30000 },
      );
      setMeta({
        pct:          Number(data?.completeness || 0),
        tier:         String(data?.profile_quality || 'Basic'),
        coreAnswered: Number(data?.core_answered || 0),
        coreTotal:    Number(data?.core_total || 0),
      });
    } catch (e) {
      console.warn('life-profile save failed', e);
      const status = e?.response?.status;
      if (status === 503) {
        toast.error("We're finishing a quick database update. Please try again in a minute.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not save just now. We'll retry.");
      }
    } finally {
      setSaving(false);
    }
  };

  const setKey = (key, value) => scheduleSave(setValue(bag, key, value));

  if (loading) {
    return (
      <div className="space-y-6" data-testid="life-profile-loading">
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const pct = Math.round(meta.pct * 100);

  return (
    <div className="space-y-6" data-testid="life-profile-embed">
      {saving && (
        <div className="flex justify-end -mb-4" role="status" aria-live="polite" aria-atomic="true">
          <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="life-profile-saving">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse" aria-hidden="true" />
            Saving…
          </span>
        </div>
      )}

      {/* v3.4.7 — Clarity Score. Shown only when the embed is rendered
          on its own page (`showTitle=true`); the unified Profile hub
          replaces the score+percentage stack with Data coverage /
          Planning readiness (see components/profile/ProfileReadinessCard). */}
      {showTitle && <ClarityScoreEmbed />}

      {showTitle && (
        <div className="space-y-2">
          <h2 className="font-display text-xl font-medium text-charcoal" style={{ letterSpacing: '-0.01em' }} data-testid="life-profile-title">
            Life Profile
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">
            2120 uses this information to personalise your projections, reflections
            and AI guidance. The more context you share, the more relevant your
            insights become.
          </p>
        </div>
      )}

      {/* Hero — warmer voice. Kept only on the standalone page. */}
      {showTitle && (
        <Card className="rounded-2xl border-border/60" data-testid="life-profile-hero">
          <CardContent className="p-5 space-y-3">
            <div className="text-base font-medium leading-snug">
              The more we understand your life, the more helpful 2120 becomes.
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Answer as much or as little as you&apos;re comfortable sharing. You can
              always change it later.
            </p>
          </CardContent>
        </Card>
      )}

      {/* v3.7.10 Phase 84.31 Part 4 — Old completeness percentage card
          retired. Data coverage + Planning readiness (rendered by the
          Profile hub above) replace it. On the standalone page we
          still show a soft autosave hint below. */}

      {/* Autosave hint — subtle, one line. Reassures users that the
          absence of a "Save" button is intentional. */}
      <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground" data-testid="life-profile-autosave-hint">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary/70" />
        <span>Your answers save automatically as you go.</span>
      </div>

      {/* Sections */}
      {LIFE_PROFILE_CATALOG.map((section) => {
        const visible = section.questions.filter((q) =>
          shouldShowQuestion(q, { life_profile: bag, profile }),
        );
        if (visible.length === 0) return null;
        return (
          <Card key={section.id} className="rounded-2xl border-border/60" data-testid={`life-profile-section-${section.id}`}>
            <CardContent className="p-5 space-y-5">
              <div>
                <div className="font-display text-lg font-semibold">{section.title}</div>
                {section.subtitle && (
                  <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {section.subtitle}
                  </div>
                )}
              </div>
              <div className="space-y-6">
                {visible.map((q) => (
                  <div key={q.key} data-lp-field={q.key} tabIndex={-1}>
                    <QuestionRow
                      q={q}
                      value={getValue(bag, q.key)}
                      onChange={(v) => setKey(q.key, v)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <div className="text-[11px] text-muted-foreground text-center pt-4 leading-relaxed" data-testid="life-profile-footer">
        Only you can see this information. You can update or remove it at any time.
      </div>
    </div>
  );
}

export default function SettingsLifeProfile() {
  usePageTitle('Life Profile');
  const nav = useNavigate();

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 pb-16 space-y-6" data-testid="life-profile-page">
      {/* Header with back button — only shown on the standalone page. The
          embedded version inside /settings does not render this. */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav('/settings')}
          className="rounded-xl -ml-2"
          data-testid="life-profile-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Settings
        </Button>
      </div>

      {/* Title + benefit sentence — page-specific voice; embed uses its own. */}
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-semibold">
          Know you a little better
        </h1>
      </div>

      <LifeProfileEmbed showTitle={false} />
    </div>
  );
}


// =============================================================================
// QuestionRow — renders a single question by kind.
// =============================================================================
export function QuestionRow({ q, value, onChange }) {
  return (
    <div className="space-y-2.5" data-testid={`lp-q-${q.key}`}>
      <div className="text-sm font-medium">{q.prompt}</div>
      {q.helper && (
        <div className="text-xs text-muted-foreground -mt-1 leading-relaxed">
          {q.helper}
        </div>
      )}
      {q.kind === 'single_choice' && (
        <ChipRow
          options={q.options || []}
          selected={value}
          onSelect={(v) => onChange(v === value ? undefined : v)}
        />
      )}
      {q.kind === 'multi_choice' && (
        <ChipRow
          options={q.options || []}
          multi
          selected={Array.isArray(value) ? value : []}
          onSelect={(v) => {
            const arr = Array.isArray(value) ? [...value] : [];
            const i = arr.indexOf(v);
            if (i === -1) arr.push(v);
            else arr.splice(i, 1);
            onChange(arr.length ? arr : undefined);
          }}
        />
      )}
      {q.kind === 'multi_choice_ranked' && (
        <RankedPicker
          options={q.options || []}
          selected={Array.isArray(value) ? value : []}
          max={q.max_selected || 3}
          onChange={onChange}
        />
      )}
      {q.kind === 'age_chips' && (
        <AgeChipsPicker
          value={value}
          onChange={onChange}
          allowPnts={q.allow_pnts}
        />
      )}
      {(q.kind === 'emoji_scale' || q.kind === 'scale_1_to_5') && (
        <EmojiScale value={value} onChange={onChange} />
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Chip primitives
// -----------------------------------------------------------------------------
function ChipRow({ options, selected, onSelect, multi = false }) {
  const isActive = (v) => (multi ? (selected || []).includes(v) : selected === v);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = isActive(o.value);
        return (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => onSelect(o.value)}
            data-testid={`lp-chip-${o.value}`}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
              active
                ? 'border-primary bg-primary/10 text-primary font-medium'
                : (o.value === PNTS
                    ? 'border-dashed border-border/60 text-muted-foreground hover:border-border'
                    : 'border-border/70 hover:border-border bg-card')
            }`}
          >
            {active && <Check className="inline h-3 w-3 -mt-0.5 mr-1" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Ranked multi-choice — clicking an option appends to the ordered list;
// click again to remove. The rank badge (1/2/3) shows selection order.
// -----------------------------------------------------------------------------
function RankedPicker({ options, selected, max, onChange }) {
  const toggle = (v) => {
    const arr = [...selected];
    const i = arr.indexOf(v);
    if (i !== -1) {
      arr.splice(i, 1);
    } else {
      if (arr.length >= max) {
        toast.info(`Pick up to ${max} — tap a highlighted one to remove it first.`);
        return;
      }
      arr.push(v);
    }
    onChange(arr.length ? arr : undefined);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const rank = selected.indexOf(o.value);
        const active = rank !== -1;
        return (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => toggle(o.value)}
            data-testid={`lp-chip-${o.value}`}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
              active
                ? 'border-primary bg-primary/10 text-primary font-medium'
                : 'border-border/70 hover:border-border bg-card'
            }`}
          >
            {active && (
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                {rank + 1}
              </span>
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}


// -----------------------------------------------------------------------------
// AgeChipsPicker — "None" + numeric chips. Kept intentionally simple.
// -----------------------------------------------------------------------------
function AgeChipsPicker({ value, onChange, allowPnts }) {
  const isPnts = value === PNTS;
  const arr = Array.isArray(value) ? value : [];
  const hasNone = arr.length === 0 && !isPnts && value !== undefined;

  const [entry, setEntry] = useState('');

  const setNone = () => onChange([]);   // explicit "no children"
  const setPntsChoice = () => onChange(PNTS);
  const removeAge = (age) => {
    const next = arr.filter((a) => a !== age);
    onChange(next.length ? next : []);
  };
  const addAge = () => {
    const n = parseInt(entry, 10);
    if (!Number.isFinite(n) || n < 0 || n > 25) {
      toast.info('Enter an age between 0 and 25.');
      return;
    }
    const next = [...arr, n].sort((a, b) => a - b);
    onChange(next);
    setEntry('');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={setNone}
          data-testid="lp-age-none"
          className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
            hasNone ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border/70 hover:border-border bg-card'
          }`}
        >
          {hasNone && <Check className="inline h-3 w-3 -mt-0.5 mr-1" />}
          No children
        </button>
        {arr.map((age) => (
          <span
            key={age}
            className="text-sm px-3 py-1.5 rounded-full border border-primary bg-primary/10 text-primary font-medium flex items-center gap-1.5"
            data-testid={`lp-age-chip-${age}`}
          >
            Age {age}
            <button
              type="button"
              onClick={() => removeAge(age)}
              className="opacity-70 hover:opacity-100"
              aria-label={`Remove age ${age}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {allowPnts && (
          <button
            type="button"
            onClick={setPntsChoice}
            data-testid="lp-age-pnts"
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors border-dashed ${
              isPnts ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border/60 text-muted-foreground hover:border-border'
            }`}
          >
            {isPnts && <Check className="inline h-3 w-3 -mt-0.5 mr-1" />}
            Prefer not to say
          </button>
        )}
      </div>
      {!isPnts && (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            inputMode="numeric"
            placeholder="Add an age"
            className="w-32 rounded-xl h-8 text-sm"
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addAge()}
            min={0}
            max={25}
            data-testid="lp-age-input"
          />
          <Button type="button" variant="ghost" size="sm" onClick={addAge} className="rounded-xl h-8 text-xs" data-testid="lp-age-add-btn">
            Add
          </Button>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// EmojiScale — 5 large colored cards with persistent labels underneath.
// Values 1..5 map to Stressed / Struggling / Okay / Doing well / In control.
// The color ramp (red → amber → neutral → green → deep green) is intentional:
// it makes the scale readable at a glance without relying on emoji alone,
// and matches the design reference for this question.
// -----------------------------------------------------------------------------
const EMOJI_STEPS = [
  { value: 1, emoji: '😟', label: 'Stressed',    tone: 'red' },
  { value: 2, emoji: '😕', label: 'Struggling',  tone: 'orange' },
  { value: 3, emoji: '🙂', label: 'Okay',        tone: 'neutral' },
  { value: 4, emoji: '😀', label: 'Doing well',  tone: 'green' },
  { value: 5, emoji: '😁', label: 'In control',  tone: 'emerald' },
];

// Tailwind class map for each tone. Kept in one place so it stays
// consistent across borders / rings / labels and so we never trip on
// Tailwind's JIT purge (all classes are static strings here).
const TONE_STYLES = {
  red: {
    idle:   'border-red-300/60 bg-red-50/60 hover:border-red-400 dark:bg-red-950/20 dark:border-red-900/50',
    active: 'border-red-500 bg-red-100/70 ring-2 ring-red-400/40 dark:bg-red-950/40 dark:border-red-400',
    label:  'text-red-700 dark:text-red-300',
  },
  orange: {
    idle:   'border-orange-300/60 bg-orange-50/60 hover:border-orange-400 dark:bg-orange-950/20 dark:border-orange-900/50',
    active: 'border-orange-500 bg-orange-100/70 ring-2 ring-orange-400/40 dark:bg-orange-950/40 dark:border-orange-400',
    label:  'text-orange-700 dark:text-orange-300',
  },
  neutral: {
    idle:   'border-amber-300/60 bg-amber-50/60 hover:border-amber-400 dark:bg-amber-950/20 dark:border-amber-900/50',
    active: 'border-amber-500 bg-amber-100/70 ring-2 ring-amber-400/40 dark:bg-amber-950/40 dark:border-amber-400',
    label:  'text-amber-700 dark:text-amber-300',
  },
  green: {
    idle:   'border-green-300/60 bg-green-50/60 hover:border-green-400 dark:bg-green-950/20 dark:border-green-900/50',
    active: 'border-green-500 bg-green-100/70 ring-2 ring-green-400/40 dark:bg-green-950/40 dark:border-green-400',
    label:  'text-green-700 dark:text-green-300',
  },
  emerald: {
    idle:   'border-emerald-400/60 bg-emerald-50/60 hover:border-emerald-500 dark:bg-emerald-950/20 dark:border-emerald-900/50',
    active: 'border-emerald-600 bg-emerald-100/70 ring-2 ring-emerald-500/40 dark:bg-emerald-950/40 dark:border-emerald-400',
    label:  'text-emerald-700 dark:text-emerald-300',
  },
};

function EmojiScale({ value, onChange }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-2 sm:gap-3">
        {EMOJI_STEPS.map((s) => {
          const active = value === s.value;
          const tone = TONE_STYLES[s.tone];
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => onChange(active ? undefined : s.value)}
              data-testid={`lp-emoji-${s.value}`}
              aria-label={s.label}
              aria-pressed={active}
              className={`flex flex-col items-center gap-1.5 rounded-2xl border p-3 sm:p-4 transition-colors ${
                active ? tone.active : tone.idle
              }`}
            >
              <span className="text-3xl sm:text-4xl leading-none" aria-hidden>{s.emoji}</span>
              <span className={`text-[11px] sm:text-xs font-medium leading-tight text-center ${active ? tone.label : 'text-foreground/80'}`}>
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => onChange(value === PNTS ? undefined : PNTS)}
          data-testid="lp-emoji-pnts"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors border-dashed ${
            value === PNTS ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border/60 text-muted-foreground hover:border-border'
          }`}
        >
          Prefer not to say
        </button>
      </div>
    </div>
  );
}
